import { Component, HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { ComponentHasUnsavedChanges } from '../../../../../../unsaved-changes-guard.service';
import {
  CustomInbox, CustomInboxCondition,
  CustomInboxConditionType,
  CustomInboxStatus,
  getCustomInboxConditionDescription,
  getCustomInboxStatusDisplay
} from '../../../../../../models/custom-inbox';
import { AccountService } from '../../../../../../services/account/account.service';
import { CustomInboxService } from '../../../../../../services/custom-inbox/custom-inbox.service';
import { MyAccount } from '../../../../../../models/my-account';
import { LocationService } from '../../../../../../services/location/location.service';
import { CustomInboxConditionModalComponent } from './modals/custom-inbox-condition-modal/custom-inbox-condition-modal.component';
import { StandardColorRed } from '../../../../../../utils';
import { EntryPassService } from '../../../../../../services/entry-pass/entry-pass.service';
import { EntryPass } from '../../../../../../models/entry-pass';
import { Membership } from '../../../../../../models/membership';
import { MembershipService } from '../../../../../../services/membership/membership.service';


@Component({
  selector: 'app-custom-inbox-edit',
  templateUrl: './custom-inbox-edit.component.html',
  styleUrls: ['./custom-inbox-edit.component.css']
})
export class CustomInboxEditComponent implements OnInit, ComponentHasUnsavedChanges {
  public isNewCustomInbox: boolean;
  public customInbox: CustomInbox = {
    id: null,
    url: '',
    name: '',
    status: CustomInboxStatus.draft,
    conditions: [
      {
        condition_type: CustomInboxConditionType.hasNotBeenInInboxPreviously,
        parameters: {}
      }
    ],
    include_customer_profile_alert: false,
    customer_profile_alert_color_hex: StandardColorRed.colorHex,
    prevent_customer_check_in: false,
    auto_resolve_time_hours: null,
    staff_instruction_text: '',
    num_active_items: 0,
    num_all_time_items: 0,
    created_at: '',
    updated_at: ''
  };

  // If this is an existing product, store the existing status to determine
  // whether changing "Draft-edit-only" fields is allowed. We cannot simply
  // read the status of the product object because then any restricted
  // actions would take effect as soon as the status is changed locally, rather
  // than upon saving.
  public existingStatus: CustomInboxStatus;

  public myAccount: MyAccount;

  public entryPasses: EntryPass[];
  public memberships: Membership[];

  public loadingExistingCustomInbox = false;
  public loadingEntryPasses = true;
  public loadingMemberships = true;
  public loadingSubmit = false;

  public errorMessage = '';
  public errors: any = {};
  public notFound = false;
  public serverError: number = null;
  public unsavedChanges = false;

  public readonly getCustomInboxStatusDisplay = getCustomInboxStatusDisplay;
  public readonly CustomInboxStatus = CustomInboxStatus;

  constructor(
    public accountService: AccountService,
    public locationService: LocationService,
    private entryPassService: EntryPassService,
    private membershipService: MembershipService,
    private route: ActivatedRoute,
    private router: Router,
    private customInboxService: CustomInboxService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewCustomInbox = !routeParams.hasOwnProperty('id');

    this.myAccount = this.accountService.getMyAccount();
    if (!this.myAccount.is_organization_admin) { return; }

    if (!this.isNewCustomInbox) {
      const idInt = parseInt(routeParams.id, 10);
      if (isNaN(idInt)) {
        this.notFound = true;
        this.loadingExistingCustomInbox = false;
        return;
      }

      this.loadCustomInbox(idInt);
    }

    this.loadEntryPasses();
    this.loadMemberships();
  }

  hasUnsavedChanges() {
    return this.unsavedChanges;
  }

  @HostListener('window:beforeunload')
  allowUnload() {
    return !this.hasUnsavedChanges();
  }

  public handleSubmit() {
    if (this.loadingSubmit) { return; }

    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    let savePromise: Promise<CustomInbox>;
    if (this.isNewCustomInbox) {
      savePromise = this.customInboxService.addCustomInbox(this.customInbox);
    } else {
      savePromise = this.customInboxService.updateCustomInbox(this.customInbox);
    }

    savePromise
      .then(savedCustomInbox => {
        this.customInbox = savedCustomInbox;

        this.unsavedChanges = false;

        let successMessage: string;
        if (this.isNewCustomInbox) {
          successMessage = this.customInbox.name + ' created successfully.';
        } else {
          successMessage = this.customInbox.name + ' updated successfully.';
        }

        this.router.navigate(
          ['/admin/custom-inboxes'],
          {
            state: {
              feedback: {
                text: successMessage,
                status: 'success'
              }
            }
          }
        );
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors shown and try again.';
        } else if (reason.status === 403) {
          if (this.isNewCustomInbox) {
            this.errorMessage = 'You do not have permission to add custom inboxes.';
          } else {
            this.errorMessage = 'You do not have permission to modify this custom inbox.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

  public getAvailableStatuses(): CustomInboxStatus[] {
    if (this.isExistingNonDraftProduct()) {
      return [CustomInboxStatus.active, CustomInboxStatus.retired];
    } else {
      return [CustomInboxStatus.draft, CustomInboxStatus.active, CustomInboxStatus.retired];
    }
  }

  public isExistingNonDraftProduct(): boolean {
    return !this.isNewCustomInbox && (this.existingStatus !== CustomInboxStatus.draft);
  }

  public editCondition(condition: CustomInboxCondition) {
    if (this.loadingExistingCustomInbox) { return; }
    if (this.isLoadingConditionParameterQueries()) { return; }

    const modalRef = this.modalService.open(
      CustomInboxConditionModalComponent
    );
    modalRef.componentInstance.inputCondition = condition;
    modalRef.componentInstance.locations = this.locationService.getLocations();
    modalRef.componentInstance.entryPasses = this.entryPasses;
    modalRef.componentInstance.memberships = this.memberships;
    modalRef.componentInstance.completedCallback = (result: CustomInboxCondition) => {
      this.unsavedChanges = true;
      condition.condition_type = result.condition_type;
      condition.parameters = result.parameters;
    };
  }

  public addCondition() {
    if (this.loadingExistingCustomInbox) { return; }
    if (this.isLoadingConditionParameterQueries()) { return; }

    const modalRef = this.modalService.open(
      CustomInboxConditionModalComponent
    );
    modalRef.componentInstance.locations = this.locationService.getLocations();
    modalRef.componentInstance.entryPasses = this.entryPasses;
    modalRef.componentInstance.memberships = this.memberships;
    modalRef.componentInstance.completedCallback = (result: CustomInboxCondition) => {
      this.unsavedChanges = true;
      this.customInbox.conditions.push(result);
    };

  }

  public removeCondition(conditionIdx: number) {
    if (this.loadingExistingCustomInbox) { return; }

    this.unsavedChanges = true;
    this.customInbox.conditions.splice(conditionIdx, 1);
  }

  public isLoadingConditionParameterQueries() {
    return (
      (this.locationService.getLocations() === null) ||
      this.loadingEntryPasses ||
      this.loadingMemberships
    );
  }

  public getCustomInboxConditionDescription(condition: CustomInboxCondition) {
    if (this.isLoadingConditionParameterQueries()) {
      return 'Loading...';
    }

    return getCustomInboxConditionDescription(
      condition,
      this.locationService.getLocations(),
      this.entryPasses,
      this.memberships
    );
  }

  private loadCustomInbox(id: number) {
    this.loadingExistingCustomInbox = true;

    this.customInboxService
      .getCustomInbox(id)
      .then(customInbox => {
        this.customInbox = customInbox;
        this.existingStatus = customInbox.status;
      })
      .catch(reason => {
        if (reason.status === 404) {
          this.notFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingExistingCustomInbox = false; });
  }

  private loadEntryPasses() {
    this.loadingEntryPasses = true;

    this.entryPassService
      .getEntryPasses(1, 3000, {})
      .then(result => { this.entryPasses = result.results; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingEntryPasses = false; });
  }

  private loadMemberships() {
    this.loadingMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {include_rolling: true}
      )
      .then(result => { this.memberships = result.results; })
      .catch(reason => { this.serverError = reason.status; })
      .finally(() => { this.loadingMemberships = false; });
  }
}
